import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays,
  Copy,
  FolderKanban,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ExportButton } from "@/components/ui/ExportButton";
import { ColumnSelectorButton } from "@/components/ui/ColumnSelectorButton";
import { ViewToggleButton } from "@/components/ui/ViewToggleButton";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { AppLayout } from "@/components/layout/AppLayout";
import { formatDateTime, initials } from "@/lib/utils";
import {
  useMeetingList,
  useDeleteMeeting,
  useUpdateMeetingStatus,
} from "../hooks/useMeetings";
import { NewMeetingDrawer } from "../components/NewMeetingDrawer";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { usePhaseSelector } from "@/modules/projects/hooks/usePhases";
import { useViewMode } from "@/hooks/useViewMode";
import {
  useColumnVisibility,
  type ColumnDef,
} from "@/hooks/useColumnVisibility";
import type { MeetingListItem, MeetingStatus, MeetingType } from "../types";

type StatusFilter = "" | MeetingStatus;
type TypeFilter = "" | MeetingType;

const MEETING_COLUMNS: ColumnDef[] = [
  { key: "code", label: "#" },
  { key: "title", label: "Title" },
  { key: "projectName", label: "Project" },
  { key: "scheduledAt", label: "Date" },
  { key: "type", label: "Type" },
  { key: "category", label: "Category", defaultVisible: false },
  { key: "status", label: "Status" },
  { key: "organizerName", label: "Organizer" },
];

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; fg: string; label: string }> = {
    SCHEDULED: { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)", label: "Scheduled" },
    IN_PROGRESS: { bg: "hsl(38,92%,92%)", fg: "hsl(38,92%,30%)", label: "In Progress" },
    COMPLETED: { bg: "hsl(142,55%,92%)", fg: "hsl(142,72%,28%)", label: "Completed" },
    CANCELLED: { bg: "hsl(0,84%,95%)", fg: "hsl(0,72%,45%)", label: "Cancelled" },
  };
  const s = styles[status] ?? styles.SCHEDULED;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={{ background: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
}

function TypeBadge({ type }: { type: string }) {
  const styles: Record<string, { bg: string; fg: string; label: string }> = {
    INTERNAL: { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)", label: "Internal" },
    EXTERNAL: { bg: "hsl(38,92%,92%)", fg: "hsl(38,92%,30%)", label: "External" },
    CLIENT: { bg: "hsl(262,55%,92%)", fg: "hsl(262,83%,38%)", label: "Client" },
    PARTNER: { bg: "hsl(175,55%,92%)", fg: "hsl(175,60%,30%)", label: "Partner" },
    BOARD: { bg: "hsl(340,55%,92%)", fg: "hsl(340,72%,38%)", label: "Board" },
    STANDUP: { bg: "hsl(210,16%,93%)", fg: "hsl(215,16%,40%)", label: "Stand-up" },
  };
  const s = styles[type] ?? styles.INTERNAL;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={{ background: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
}

function StatusPopover({
  meetingId,
  currentStatus,
  onClose,
}: {
  meetingId: string;
  currentStatus: string;
  onClose: () => void;
}) {
  const updateStatus = useUpdateMeetingStatus();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  const statuses: { value: MeetingStatus; label: string }[] = [
    { value: "SCHEDULED", label: "Scheduled" },
    { value: "IN_PROGRESS", label: "In Progress" },
    { value: "COMPLETED", label: "Completed" },
    { value: "CANCELLED", label: "Cancelled" },
  ];

  async function handleSelect(status: MeetingStatus) {
    if (status === currentStatus) return;
    await updateStatus.mutateAsync({ id: meetingId, status });
    onClose();
  }

  return (
    <div
      ref={ref}
      className="absolute right-0 top-8 z-50 w-44 rounded-lg border bg-white py-1 shadow-lg"
      style={{ borderColor: "hsl(210,30%,85%)" }}
    >
      {statuses.map((s) => (
        <button
          key={s.value}
          type="button"
          onClick={() => handleSelect(s.value)}
          className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-[hsl(210,50%,96%)] ${
            s.value === currentStatus ? "font-medium text-primary" : "text-foreground"
          }`}
        >
          <span
            className={`h-2 w-2 rounded-full ${
              s.value === currentStatus ? "bg-primary" : "bg-transparent border border-muted-foreground/30"
            }`}
          />
          {s.label}
        </button>
      ))}
    </div>
  );
}

function MeetingRow({
  meeting,
  onDelete,
  onDuplicate,
  isVisible,
}: {
  meeting: MeetingListItem;
  onDelete: (id: string, name: string) => void;
  onDuplicate: (m: MeetingListItem) => void;
  isVisible: (key: string) => boolean;
}) {
  const navigate = useNavigate();
  const [showStatusPopover, setShowStatusPopover] = useState(false);

  return (
    <tr
      className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
      onClick={() => navigate(`/meetings/${meeting.id}`)}
    >
      {isVisible("code") && (
        <td className="px-6 py-4">
          <span className="font-mono text-xs text-muted-foreground">{meeting.code}</span>
        </td>
      )}
      {isVisible("title") && (
        <td className="px-6 py-4">
          <div className="font-medium text-foreground">{meeting.title}</div>
        </td>
      )}
      {isVisible("projectName") && (
        <td className="px-6 py-4 text-sm text-muted-foreground">
          {meeting.projectName ? (
            <button
              type="button"
              className="flex items-center gap-1.5 hover:text-primary"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/projects/${meeting.projectId}`);
              }}
            >
              <FolderKanban className="h-3 w-3" strokeWidth={1.5} />
              {meeting.projectName}
            </button>
          ) : meeting.companyName ? (
            <span className="flex items-center gap-1.5">
              {meeting.companyName}
              {meeting.productName && (
                <span className="text-muted-foreground/50">/ {meeting.productName}</span>
              )}
            </span>
          ) : (
            "—"
          )}
        </td>
      )}
      {isVisible("scheduledAt") && (
        <td className="px-6 py-4 text-sm text-muted-foreground">
          {formatDateTime(meeting.scheduledAt)}
        </td>
      )}
      {isVisible("type") && (
        <td className="px-6 py-4">
          <TypeBadge type={meeting.type} />
        </td>
      )}
      {isVisible("category") && (
        <td className="px-6 py-4 text-sm text-muted-foreground">
          {meeting.category ?? "—"}
        </td>
      )}
      {isVisible("status") && (
        <td className="px-6 py-4">
          <StatusBadge status={meeting.status} />
        </td>
      )}
      {isVisible("organizerName") && (
        <td className="px-6 py-4 text-sm text-muted-foreground">
          {meeting.organizerName}
        </td>
      )}
      <td className="px-6 py-4">
        <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => navigate(`/meetings/${meeting.id}`)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-[hsl(210,50%,96%)] hover:text-foreground"
            title="View"
          >
            <CalendarDays className="h-[14px] w-[14px]" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={() => navigate(`/meetings/${meeting.id}?edit=true`)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-[hsl(210,50%,96%)] hover:text-foreground"
            title="Edit"
          >
            <svg className="h-[14px] w-[14px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
          </button>
          <button
            type="button"
            onClick={() => onDuplicate(meeting)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-[hsl(210,50%,96%)] hover:text-foreground"
            title="Duplicate"
          >
            <Copy className="h-[14px] w-[14px]" strokeWidth={1.5} />
          </button>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowStatusPopover(!showStatusPopover)}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-[hsl(210,50%,96%)] hover:text-foreground"
              title="Change Status"
            >
              <RefreshCw className="h-[14px] w-[14px]" strokeWidth={1.5} />
            </button>
            {showStatusPopover && (
              <StatusPopover
                meetingId={meeting.id}
                currentStatus={meeting.status}
                onClose={() => setShowStatusPopover(false)}
              />
            )}
          </div>
          <button
            type="button"
            onClick={() => onDelete(meeting.id, meeting.title)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600"
            title="Delete"
          >
            <svg className="h-[14px] w-[14px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
          </button>
        </div>
      </td>
    </tr>
  );
}

function MeetingCard({ meeting }: { meeting: MeetingListItem }) {
  const navigate = useNavigate();
  return (
    <div
      className="cursor-pointer rounded-xl border bg-card p-5 transition-colors hover:bg-[hsl(210,50%,98%)]"
      style={{ borderColor: "hsl(210,30%,85%)" }}
      onClick={() => navigate(`/meetings/${meeting.id}`)}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[hsl(210,55%,92%)] text-sm font-semibold text-[hsl(210,100%,28%)]">
          {initials(meeting.title)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-foreground">{meeting.title}</div>
          <div className="mt-0.5 font-mono text-xs text-muted-foreground">{meeting.code}</div>
        </div>
      </div>
      <div className="mt-2 text-xs text-muted-foreground">
        {formatDateTime(meeting.scheduledAt)}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <TypeBadge type={meeting.type} />
        <StatusBadge status={meeting.status} />
      </div>
      <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
        {meeting.projectName && (
          <>
            <FolderKanban className="h-3 w-3" strokeWidth={1.5} />
            {meeting.projectName}
          </>
        )}
        {!meeting.projectName && meeting.companyName && meeting.companyName}
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
        <span>{meeting.organizerName}</span>
        <span className="flex items-center gap-1">
          <Users className="h-3 w-3" strokeWidth={1.5} />
          {meeting._count.attendees}
        </span>
      </div>
    </div>
  );
}

export default function MeetingListPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("");
  const [projectFilter, setProjectFilter] = useState<string>("");
  const [phaseFilter, setPhaseFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const { mode: viewMode, toggle: toggleViewMode, isGrid } = useViewMode(
    "meetings",
    "list",
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [duplicateTarget, setDuplicateTarget] = useState<MeetingListItem | null>(
    null,
  );
  const [toast, setToast] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const {
    visibility,
    toggle: toggleColumn,
    reset: resetColumns,
    visibleKeys,
    isVisible,
    minReached,
  } = useColumnVisibility("meetings", MEETING_COLUMNS);
  const exportFilters = useMemo(
    () => ({
      search: search.trim() || undefined,
      status: statusFilter || undefined,
      type: typeFilter || undefined,
      projectId: projectFilter || undefined,
      phaseId: phaseFilter || undefined,
    }),
    [search, statusFilter, typeFilter, projectFilter, phaseFilter],
  );

  const deleteMeeting = useDeleteMeeting();

  const {
    data: result,
    isLoading,
    isError,
    error,
  } = useMeetingList({
    page,
    limit,
    search: search.trim() || undefined,
    status: statusFilter || undefined,
    type: typeFilter || undefined,
    projectId: projectFilter || undefined,
    phaseId: phaseFilter || undefined,
  });

  const { data: projectsResult } = useProjectList({ page: 1, limit: 100 });
  const projectOptions = projectsResult?.data ?? [];
  const { data: phaseOptions } = usePhaseSelector(projectFilter || null);

  const meetings = result?.data ?? [];
  const pagination = result?.pagination ?? {
    page: 1,
    limit,
    total: 0,
    totalPages: 1,
  };
  const total = pagination.total;

  function resetPage<T>(setter: (v: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  }

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  function handleDelete(id: string, name: string) {
    setDeleteTarget({ id, name });
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    await deleteMeeting.mutateAsync(deleteTarget.id);
    showToast(`${deleteTarget.name} deleted successfully`);
  }

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Daily Work" },
        { label: "Meetings" },
      ]}
    >
      <div className="w-full space-y-6">
        {toast && (
          <div
            className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
            style={{ borderColor: "hsl(142,55%,80%)" }}
          >
            {toast}
          </div>
        )}

        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CalendarDays className="h-[18px] w-[18px] text-foreground" strokeWidth={1.5} />
            <h1 className="text-2xl font-semibold tracking-tight">Meetings</h1>
          </div>
          <div className="flex items-center gap-2">
            <ViewToggleButton mode={viewMode} onToggle={toggleViewMode} />
            <ExportButton
              module="meetings"
              filters={exportFilters}
              visibleColumns={visibleKeys}
            />
            <ColumnSelectorButton
              columns={MEETING_COLUMNS}
              visibility={visibility}
              onToggle={toggleColumn}
              onReset={resetColumns}
              minReached={minReached}
            />
            <Button onClick={() => setDrawerOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              New Meeting
            </Button>
          </div>
        </div>

        {isError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            <div className="font-medium">Failed to load meetings</div>
            <div className="mt-1 font-mono text-xs opacity-80">
              {(error as Error)?.message ?? "Unknown error"}
            </div>
          </div>
        ) : (
          <>
            {/* Filters */}
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <div className="relative w-[280px]">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <Input
                  placeholder="Search meetings..."
                  value={search}
                  onChange={(e) => resetPage(setSearch)(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={statusFilter}
                onChange={(e) => resetPage(setStatusFilter)(e.target.value as StatusFilter)}
                className="w-40"
              >
                <option value="">All Status</option>
                <option value="SCHEDULED">Scheduled</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="COMPLETED">Completed</option>
                <option value="CANCELLED">Cancelled</option>
              </Select>
              <Select
                value={typeFilter}
                onChange={(e) => resetPage(setTypeFilter)(e.target.value as TypeFilter)}
                className="w-40"
              >
                <option value="">All Types</option>
                <option value="INTERNAL">Internal</option>
                <option value="EXTERNAL">External</option>
                <option value="CLIENT">Client</option>
                <option value="PARTNER">Partner</option>
                <option value="BOARD">Board</option>
                <option value="STANDUP">Stand-up</option>
              </Select>
              <Select
                value={projectFilter}
                onChange={(e) => {
                  resetPage(setProjectFilter)(e.target.value);
                  setPhaseFilter("");
                }}
                className="w-48"
              >
                <option value="">All Projects</option>
                {projectOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
              <Select
                value={phaseFilter}
                onChange={(e) => resetPage(setPhaseFilter)(e.target.value)}
                className="w-40"
                disabled={!projectFilter}
              >
                <option value="">
                  {projectFilter ? "All Phases" : "Select a project first"}
                </option>
                {phaseOptions?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </div>

            {/* Grid view */}
            {isGrid ? (
              <div>
                {isLoading ? (
                  <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
                    Loading meetings...
                  </div>
                ) : meetings.length === 0 ? (
                  <div className="py-16 text-center text-sm text-muted-foreground">
                    No meetings match the current filters.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {meetings.map((m) => (
                      <MeetingCard key={m.id} meeting={m} />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* Table view */
              <div
                className="overflow-hidden rounded-xl border bg-card"
                style={{ borderColor: "hsl(210,30%,85%)" }}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
                    Loading meetings...
                  </div>
                ) : meetings.length === 0 ? (
                  <div className="py-16 text-center text-sm text-muted-foreground">
                    No meetings match the current filters.
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      <tr>
                        {isVisible("code") && <th className="px-6 py-3">#</th>}
                        {isVisible("title") && <th className="px-6 py-3">Title</th>}
                        {isVisible("projectName") && (
                          <th className="px-6 py-3">Project</th>
                        )}
                        {isVisible("scheduledAt") && (
                          <th className="px-6 py-3">Date</th>
                        )}
                        {isVisible("type") && <th className="px-6 py-3">Type</th>}
                        {isVisible("category") && (
                          <th className="px-6 py-3">Category</th>
                        )}
                        {isVisible("status") && (
                          <th className="px-6 py-3">Status</th>
                        )}
                        {isVisible("organizerName") && (
                          <th className="px-6 py-3">Organizer</th>
                        )}
                        <th className="px-6 py-3 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {meetings.map((m) => (
                        <MeetingRow
                          key={m.id}
                          meeting={m}
                          onDelete={handleDelete}
                          onDuplicate={setDuplicateTarget}
                          isVisible={isVisible}
                        />
                      ))}
                    </tbody>
                  </table>
                )}

                {/* Pagination footer */}
                <div data-pagination="true" style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '9px 14px',
                  borderTop: '1px solid hsl(210,30%,85%)',
                  background: 'hsl(210,30%,97%)',
                  fontSize: '12px',
                  color: 'hsl(215,16%,47%)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>
                      Showing {total === 0 ? 0 : (page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total} entries
                    </span>
                    <span style={{ marginLeft: '8px' }}>Show</span>
                    <select
                      value={limit}
                      onChange={e => { setLimit(Number(e.target.value)); setPage(1) }}
                      style={{
                        height: '26px',
                        padding: '0 4px',
                        border: '1px solid hsl(210,30%,85%)',
                        borderRadius: '4px',
                        fontSize: '12px',
                        background: 'white',
                        cursor: 'pointer',
                      }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span>entries</span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <button
                      onClick={() => setPage(p => p - 1)}
                      disabled={page === 1}
                      style={{
                        width: '28px', height: '28px',
                        border: '1px solid hsl(210,30%,85%)',
                        borderRadius: '4px',
                        background: 'white',
                        cursor: page === 1 ? 'not-allowed' : 'pointer',
                        opacity: page === 1 ? 0.4 : 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >‹</button>
                    <span style={{ padding: '0 10px' }}>
                      {Math.ceil(total / limit) === 0 ? '0' : page} / {Math.ceil(total / limit) || 1}
                    </span>
                    <button
                      onClick={() => setPage(p => p + 1)}
                      disabled={page >= Math.ceil(total / limit)}
                      style={{
                        width: '28px', height: '28px',
                        border: '1px solid hsl(210,30%,85%)',
                        borderRadius: '4px',
                        background: 'white',
                        cursor: page >= Math.ceil(total / limit) ? 'not-allowed' : 'pointer',
                        opacity: page >= Math.ceil(total / limit) ? 0.4 : 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >›</button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <NewMeetingDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onCreated={(code) => showToast(`Meeting created: ${code}`)}
        />

        <NewMeetingDrawer
          isOpen={!!duplicateTarget}
          onClose={() => setDuplicateTarget(null)}
          duplicateFrom={duplicateTarget ?? undefined}
          onCreated={(code) => showToast(`Meeting created: ${code}`)}
        />

        <ConfirmDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete meeting?"
          description={
            deleteTarget
              ? `Are you sure you want to delete '${deleteTarget.name}'? It will be removed from your Meetings list.`
              : ""
          }
          onConfirm={confirmDelete}
        />
      </div>
    </AppLayout>
  );
}
